/**
 * PhotoModal - Full-screen photo viewer with carousel
 *
 * Wraps EmblaPhotoCarousel in a Modal for full-screen viewing.
 * Features zoom, navigation, dark backdrop, section tabs, and ScrollSpy sync.
 */
import { X } from "lucide-react";
import { Modal } from "@/shared/ui/components";
import { EmblaPhotoCarousel } from "../EmblaPhotoCarousel";

interface CarouselImage {
  url: string;
  alt?: string;
}

interface PhotoModalProps {
  /** Array of images to display */
  images: CarouselImage[];
  /** Index of image to start on */
  initialIndex: number;
  /** Modal open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Callback when slide changes (for ScrollSpy sync) */
  onSlideChange?: (index: number) => void;
  /** Section navigation data (for ScrollSpy) */
  sections?: string[];
  /** Currently active section derived from slide position */
  activeSection?: string;
  /** Display labels for sections */
  sectionLabels?: Record<string, string>;
  /** Handler for section tab click */
  onSectionClick?: (section: string) => void;
}

export function PhotoModal({
  images,
  initialIndex,
  open,
  onClose,
  onSlideChange,
  sections,
  activeSection,
  sectionLabels,
  onSectionClick,
}: PhotoModalProps) {
  if (!open || !images || images.length === 0) {
    return null;
  }

  const showSectionTabs = sections && sections.length > 1 && onSectionClick;

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Portal>
        {/* Full-screen overlay rendered via portal to document.body */}
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          onClick={(e) => {
            // Only close if clicking the background, not the content
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          {/* Header with close button and section tabs */}
          <div className="flex-shrink-0 relative z-50">
            {/* Close button - floating top-right */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close photo viewer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Section tabs */}
            {showSectionTabs && (
              <div className="flex justify-center pt-4 pb-2">
                <div className="flex items-center gap-2 px-4 overflow-x-auto max-w-full">
                  {sections.map((section) => (
                    <button
                      key={section}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSectionClick(section);
                      }}
                      className={`
                        px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap
                        ${
                          activeSection === section
                            ? "bg-white text-gray-900 shadow-md"
                            : "bg-white/20 text-white/80 hover:bg-white/30 hover:text-white"
                        }
                      `}
                    >
                      {sectionLabels?.[section] || section}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Carousel container */}
          <div
            className="flex-1 flex items-center justify-center min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full">
              <EmblaPhotoCarousel
                images={images}
                initialSlide={initialIndex}
                mode="modal"
                onSlideChange={onSlideChange}
              />
            </div>
          </div>
        </div>
      </Modal.Portal>
    </Modal.Root>
  );
}
